import * as React from 'react';
import { useState, useEffect, useRef, useCallback } from 'react';
import { FaBell, FaCalendarAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import NotificationService, { PendingCounts } from '../services/NotificationService';

const REFRESH_INTERVAL_MS = 60_000;

function NotificationCenter(): React.JSX.Element {
  const [counts, setCounts] = useState<PendingCounts>({ absenceRequests: 0, total: 0 });
  const [open, setOpen] = useState(false);
  const [badgeDismissed, setBadgeDismissed] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    const result = await NotificationService.fetchPendingCounts();
    setCounts(result);
    setBadgeDismissed(false);
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleNavigate = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '4px' }}>
      <div
        onClick={() => { setOpen(prev => !prev); setBadgeDismissed(true); }}
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '40px',
          height: '40px',
          borderRadius: '8px',
          cursor: 'pointer',
          transition: 'background 0.15s',
        }}
        className="sidebar-link"
        data-testid="notification-bell"
      >
        <FaBell className="sidebar-icon" />
        {counts.total > 0 && !badgeDismissed && (
          <div
            data-testid="notification-badge"
            style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              minWidth: '16px',
              height: '16px',
              borderRadius: '8px',
              background: '#E7007D',
              color: 'white',
              fontSize: '10px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
              lineHeight: 1,
            }}
          >
            {counts.total > 99 ? '99+' : counts.total}
          </div>
        )}
      </div>

      {open && (
        <div
          data-testid="notification-dropdown"
          style={{
            position: 'absolute',
            left: '100%',
            top: 0,
            marginLeft: '8px',
            background: '#1e293b',
            borderRadius: '8px',
            padding: '0',
            minWidth: '260px',
            zIndex: 1000,
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: '-4px',
              top: '16px',
              transform: 'rotate(45deg)',
              width: '8px',
              height: '8px',
              background: '#1e293b',
            }}
          />

          <div
            style={{
              padding: '12px 16px 8px',
              borderBottom: '1px solid rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ color: 'white', fontSize: '13px', fontWeight: 600 }}>Notifications</span>
            {counts.total > 0 && (
              <span
                style={{
                  background: '#E7007D',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 700,
                  borderRadius: '10px',
                  padding: '2px 8px',
                }}
              >
                {counts.total}
              </span>
            )}
          </div>

          {counts.total === 0 ? (
            <div style={{ padding: '20px 16px', textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '13px' }}>
              All caught up!
            </div>
          ) : (
            <div style={{ padding: '6px 0' }}>
              {counts.absenceRequests > 0 && (
                <button
                  data-testid="notification-absences"
                  onClick={() => handleNavigate('/absences')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    width: '100%',
                    padding: '10px 16px',
                    border: 'none',
                    background: 'transparent',
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'background 0.15s ease',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <div
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '6px',
                      background: 'rgba(231, 0, 125, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <FaCalendarAlt size={12} style={{ color: '#E7007D' }} />
                  </div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{counts.absenceRequests} pending absence request{counts.absenceRequests !== 1 ? 's' : ''}</div>
                    <div style={{ fontSize: '11px', opacity: 0.6, marginTop: '2px' }}>Click to review</div>
                  </div>
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default NotificationCenter;
